<template>
  <div class="scheme-node-rules">
    <v-container fluid>
      <div class="d-flex align-center flex-wrap mb-3" style="grid-column-gap: 16px; grid-row-gap: 8px">
        <span class="text-subtitle-1 font-weight-medium">Правила соответствия узлов</span>
        <v-autocomplete
          :value="centralNodeId"
          :items="leafNodes"
          :item-text="nodeText"
          item-value="id"
          label="Центральный узел"
          placeholder="Выберите узел"
          dense
          hide-details
          outlined
          clearable
          style="max-width: 340px"
          @change="onSelectCentral"
        />
      </div>

      <div v-if="!centralNode" class="grey--text pa-6 text-center">
        Выберите центральный узел, чтобы начать построение схемы.
      </div>

      <div v-else class="canvas-wrap">
        <!-- Канва: центральный SVG + 4 стороны -->
        <div class="canvas-grid">
          <div class="canvas-cell canvas-cell--corner"></div>
          <SideBlock
            side="top"
            :connectors="sides.top"
            :active="activeSide === 'top'"
            :node-of="nodeById"
            :loading="loading"
            :no-anchor="!hasAnchor('top')"
            @activate="setActiveSide('top')"
            @add="openPicker('top')"
            @remove="removeConnector({ id: $event })"
            @open-node="onSelectCentral($event)"
          />
          <div class="canvas-cell canvas-cell--corner"></div>

          <SideBlock
            side="left"
            :connectors="sides.left"
            :active="activeSide === 'left'"
            :node-of="nodeById"
            :loading="loading"
            :no-anchor="!hasAnchor('left')"
            @activate="setActiveSide('left')"
            @add="openPicker('left')"
            @remove="removeConnector({ id: $event })"
            @open-node="onSelectCentral($event)"
          />

          <div class="canvas-cell canvas-cell--center">
            <div class="central-frame">
              <v-img
                v-if="centralNode._file && centralNode._file.url"
                :src="centralNode._file.url"
                contain
                width="100%"
                height="100%"
              />
              <v-icon v-else size="96" color="grey lighten-1">
                mdi-cog-outline
              </v-icon>
            </div>
            <div class="central-node__label">
              <IconTooltip
                icon-text="mdi-open-in-new"
                text-tooltip="Открыть узел в «Узлах схемы»"
                color-icon="#5D80B5"
                size-icon="16"
                @click-icon="openCentralNodeInSchemeNodes"
              />
              <span class="central-node__code">{{ centralNode.code }}</span>
              <span v-if="centralNode.name_ru" class="central-node__name">
                {{ centralNode.name_ru }}
              </span>
            </div>
          </div>

          <SideBlock
            side="right"
            :connectors="sides.right"
            :active="activeSide === 'right'"
            :node-of="nodeById"
            :loading="loading"
            :no-anchor="!hasAnchor('right')"
            @activate="setActiveSide('right')"
            @add="openPicker('right')"
            @remove="removeConnector({ id: $event })"
            @open-node="onSelectCentral($event)"
          />

          <div class="canvas-cell canvas-cell--corner"></div>
          <SideBlock
            side="bottom"
            :connectors="sides.bottom"
            :active="activeSide === 'bottom'"
            :node-of="nodeById"
            :loading="loading"
            :no-anchor="!hasAnchor('bottom')"
            @activate="setActiveSide('bottom')"
            @add="openPicker('bottom')"
            @remove="removeConnector({ id: $event })"
            @open-node="onSelectCentral($event)"
          />
          <div class="canvas-cell canvas-cell--corner"></div>
        </div>
      </div>
    </v-container>

    <!-- Модалка выбора узла: открывается по «+» в блоке стороны -->
    <v-dialog v-model="pickerDialog" max-width="680">
      <v-card>
        <v-card-title class="py-2">
          <span>Добавить узел</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <v-alert
            v-if="pickerError"
            type="error"
            dense
            text
            class="mb-2"
          >
            {{ pickerError }}
          </v-alert>
          <v-text-field
            v-model="pickerSearch"
            prepend-inner-icon="mdi-magnify"
            placeholder="Поиск"
            dense
            hide-details
            outlined
            clearable
            autofocus
            class="mb-2"
          />
          <div class="grey--text text-caption mb-1">
            Добавляется в блок:
            <span class="font-weight-medium">{{ sideLabel(activeSide) }}</span>
          </div>
          <div class="picker__list">
            <div
              v-for="node in pickerNodes"
              :key="node.id"
              class="picker__row"
              :class="{
                'picker__row--used': activeSideUsedIds.has(node.id),
                'picker__row--loading': pickingNodeId === node.id,
              }"
              @click="onPickNode(node)"
            >
              <v-progress-circular
                v-if="pickingNodeId === node.id"
                indeterminate
                size="22"
                width="2"
                color="green"
                class="picker__row-spinner"
              />
              <v-img
                v-if="node._file && node._file.url"
                :src="node._file.url"
                contain
                width="150"
                height="200"
                class="picker__thumb flex-grow-0"
              />
              <div v-else class="picker__thumb picker__thumb--empty flex-grow-0">
                <v-icon size="64" color="grey lighten-1">mdi-cog-outline</v-icon>
              </div>
              <div class="picker__row-text">
                <div class="picker__row-code">{{ node.code }}</div>
                <div
                  v-if="node.name_ru"
                  class="grey--text text-caption text-truncate"
                >
                  {{ node.name_ru }}
                </div>
                <div
                  v-if="activeSideUsedIds.has(node.id)"
                  class="picker__row-added"
                >
                  уже на этой стороне
                </div>
              </div>
              <div
                v-if="
                  nodeMetaById[node.id] &&
                  (nodeMetaById[node.id].title || nodeMetaById[node.id].text)
                "
                class="picker__row-desc grey--text text-caption"
              >
                <div
                  v-if="nodeMetaById[node.id].title"
                  class="picker__row-desc-title"
                >
                  {{ nodeMetaById[node.id].title }}
                </div>
                <div v-if="nodeMetaById[node.id].text">
                  {{ nodeMetaById[node.id].text }}
                </div>
              </div>
            </div>
            <div v-if="!pickerNodes.length" class="grey--text text-caption pa-2">
              Ничего не найдено
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="pickerDialog = false">Закрыть</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import SideBlock from "./SideBlock.vue";
import IconTooltip from "@/components/common/IconTooltip.vue";

const SIDE_LABELS = {
  top: "подключение сверху",
  left: "подключение слева",
  bottom: "подключение снизу",
  right: "подключение справа",
};

export default {
  name: "SchemeNodeRules",
  components: { SideBlock, IconTooltip },
  data: () => ({
    pickerDialog: false,
    pickerSearch: "",
    pickerError: "",
    pickingNodeId: null,
  }),
  computed: {
    ...mapState("SchemeNodesModule", { tree: "tree" }),
    ...mapState("SchemeNodeRulesModule", [
      "centralNodeId",
      "activeSide",
      "loading",
    ]),
    ...mapGetters("SchemeNodeRulesModule", {
      sides: "currentSides",
      usedNodeIdsBySide: "usedNodeIdsBySide",
    }),
    // занятые узлы на стороне, в которую сейчас добавляем (модалка)
    activeSideUsedIds() {
      return this.usedNodeIdsBySide(this.activeSide);
    },
    leafNodes() {
      const flat = [];
      const walk = (nodes) => {
        for (const node of nodes || []) {
          if (!node.is_folder) flat.push(node);
          if (node.children) walk(node.children);
        }
      };
      walk(this.tree);
      return flat;
    },
    nodeMap() {
      const map = {};
      this.leafNodes.forEach((n) => (map[n.id] = n));
      return map;
    },
    allNodesById() {
      const map = {};
      const walk = (nodes) => {
        for (const node of nodes || []) {
          map[node.id] = node;
          if (node.children) walk(node.children);
        }
      };
      walk(this.tree);
      return map;
    },
    // Описание для строки пикера: своё примечание узла, иначе — легенда
    // родительской категории (название + примечание).
    nodeMetaById() {
      const map = {};
      this.leafNodes.forEach((n) => {
        const parent = n.id_parent ? this.allNodesById[n.id_parent] : null;
        map[n.id] = {
          title:
            n.name_ru || (parent && (parent.name_ru || parent.code)) || "",
          text: n.description_ru || (parent && parent.description_ru) || "",
        };
      });
      return map;
    },
    centralNode() {
      return this.centralNodeId ? this.nodeMap[this.centralNodeId] || null : null;
    },
    sortedLeafNodes() {
      return [...this.leafNodes].sort((a, b) =>
        (a.code || "").toLowerCase().localeCompare((b.code || "").toLowerCase(), "ru")
      );
    },
    pickerNodes() {
      const q = (this.pickerSearch || "").trim().toLowerCase();
      return this.sortedLeafNodes.filter((n) => {
        if (n.id === this.centralNodeId) return false;
        if (!q) return true;
        return (
          (n.code || "").toLowerCase().includes(q) ||
          (n.name_ru || "").toLowerCase().includes(q) ||
          (n.name_en || "").toLowerCase().includes(q)
        );
      });
    },
  },
  async mounted() {
    if (!this.tree.length) await this.getTree();
    this.applyRouteCode(this.$route.params.code);
  },
  watch: {
    // переход по прямой ссылке / назад-вперёд в браузере
    "$route.params.code"(code) {
      this.applyRouteCode(code);
    },
    // дерево узлов подгрузилось уже после mount — применяем код из URL
    leafNodes() {
      if (!this.centralNodeId) this.applyRouteCode(this.$route.params.code);
    },
  },
  methods: {
    ...mapActions("SchemeNodesModule", ["getTree"]),
    ...mapActions("SchemeNodeRulesModule", [
      "selectCentralNode",
      "addNodeToSide",
      "removeConnector",
    ]),
    ...mapMutations("SchemeNodeRulesModule", ["setActiveSide"]),
    nodeText(node) {
      return node.name_ru ? `${node.code} — ${node.name_ru}` : node.code;
    },
    nodeById(id) {
      return this.nodeMap[id] || null;
    },
    sideLabel(side) {
      return SIDE_LABELS[side] || side;
    },
    nodeByCode(code) {
      if (!code) return null;
      const norm = String(code).toLowerCase();
      return this.leafNodes.find((n) => (n.code || "").toLowerCase() === norm) || null;
    },
    applyRouteCode(code) {
      const node = this.nodeByCode(code);
      if (node && node.id !== this.centralNodeId) {
        this.selectCentralNode(node.id);
      }
    },
    onSelectCentral(id) {
      this.selectCentralNode(id || null);
      const node = id ? this.nodeMap[id] : null;
      const code = node && node.code ? node.code.toLowerCase() : null;
      const target = code
        ? `/scheme-node-rules/${encodeURIComponent(code)}`
        : "/scheme-node-rules";
      if (this.$route.path !== target) {
        this.$router.push(target).catch((err) => {
          // NavigationDuplicated — норм, остальное показываем
          if (err && err.name !== "NavigationDuplicated") {
            console.error("scheme-node-rules: не удалось сменить URL", err);
          }
        });
      }
    },
    // Труба центрального узла физически не доходит до этого края SVG
    // (anchors[side] определяются автоматически, см. SchemeNodeAnchorsDetector) —
    // пристыковать с этой стороны нечего.
    hasAnchor(side) {
      const anchors = this.centralNode && this.centralNode.anchors;
      return Boolean(anchors && anchors[side] && anchors[side].length);
    },
    openCentralNodeInSchemeNodes() {
      if (!this.centralNode) return;
      const routeData = this.$router.resolve({
        path: "/scheme-nodes-tree",
        query: { open: this.centralNode.id },
      });
      window.open(routeData.href, "_blank");
    },
    openPicker(side) {
      if (!this.hasAnchor(side)) return;
      this.setActiveSide(side);
      this.pickerSearch = "";
      this.pickerError = "";
      this.pickingNodeId = null;
      this.pickerDialog = true;
    },
    async onPickNode(node) {
      if (this.pickingNodeId) return;
      if (this.activeSideUsedIds.has(node.id)) return;
      this.pickerError = "";
      this.pickingNodeId = node.id;
      try {
        const response = await this.addNodeToSide({
          side: this.activeSide,
          id_node: node.id,
        });
        if (response && response.isError) {
          this.pickerError = response.message || "Не удалось добавить узел";
        }
      } finally {
        this.pickingNodeId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.scheme-node-rules {
  padding: 15px;
  min-height: 100%;
  /* холодный светло-серо-голубой — заметно темнее белых карточек и #fafafa
     side-block'ов, но не спорит с зелёным (активная сторона) и цветами труб */
  background: #e7ecf1;
}

.canvas-wrap {
  overflow-x: auto;
}

.canvas-grid {
  display: grid;
  /* все три колонки одинаковой ширины — без пустого пространства по бокам центра */
  grid-template-columns: repeat(3, 320px);
  grid-template-rows: auto auto auto;
  grid-gap: 12px;
  width: fit-content;
}

.canvas-cell {
  min-width: 0;
}

.canvas-cell--corner {
  min-height: 20px;
}

/* Блок центрального узла — квадрат по ширине колонки (≈ высоте бокового блока). */
.canvas-cell--center {
  position: relative;
  width: 320px;
  height: 320px;
  box-sizing: border-box;
}

.central-frame {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #cfd8dc;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.central-frame ::v-deep .v-image {
  width: 100%;
  height: 100%;
}

.central-node__label {
  position: absolute;
  left: 10px;
  bottom: 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  grid-column-gap: 6px;
  grid-row-gap: 2px;
  max-width: calc(100% - 20px);
  padding: 3px 10px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #eeeeee;
  border-radius: 12px;
  text-align: left;
}

.central-node__code {
  font-weight: 600;
  font-size: 15px;
}

.central-node__name {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

.picker__list {
  /* фиксированная высота — модалка не прыгает при поиске/пустом списке */
  height: 62vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.picker__row {
  position: relative;
  display: flex;
  align-items: center;
  grid-column-gap: 14px;
  padding: 8px 10px;
  margin-bottom: 6px;
  border: 1px solid #d7e6d9;
  border-left: 4px solid #4caf50;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: background-color 0.12s, box-shadow 0.12s;
}

.picker__row:last-child {
  margin-bottom: 0;
}

/* строка в процессе добавления — блок кликов + спиннер по центру */
.picker__row--loading {
  pointer-events: none;
}

.picker__row--loading > :not(.picker__row-spinner) {
  opacity: 0.4;
}

.picker__row-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.picker__thumb {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
}

.picker__thumb--empty {
  width: 150px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.picker__row:hover {
  background-color: rgba(76, 175, 80, 0.14);
  box-shadow: 0 0 0 1px #4caf50 inset;
}

/* уже добавленные — явно приглушены и не кликаются */
.picker__row--used,
.picker__row--used:hover {
  border-color: #e0e0e0;
  border-left-color: #bdbdbd;
  background: #f2f2f3;
  box-shadow: none;
  opacity: 0.55;
  filter: grayscale(1);
  cursor: not-allowed;
}

.picker__row--used .picker__row-code {
  color: rgba(0, 0, 0, 0.55);
}

.picker__row-added {
  display: inline-block;
  margin-top: 4px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #757575;
  background: #e0e0e0;
  border-radius: 10px;
}

.picker__row-text {
  flex: 0 0 auto;
  width: 160px;
  min-width: 0;
}

.picker__row-code {
  font-weight: 700;
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker__row-desc {
  flex: 1 1 auto;
  min-width: 0;
  align-self: center;
  line-height: 1.35;
}

.picker__row-desc-title {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.72);
  margin-bottom: 2px;
}
</style>
